import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { api, setAccessToken } from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import { Building2, LogOut, ExternalLink } from "lucide-react";

const StaffDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, setStaffOrgAccess } = useAuth();
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Fetch organizations list
      const orgsRes = await api.staffGetOrganizations();
      setOrganizations(orgsRes.organizations || []);
    } catch (err) {
      toast({
        title: t('staff.dashboard.loadErrorTitle'),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      
      toast({
        title: t('staff.dashboard.loggedOut'),
      });
      
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err);
      // Still navigate even on error
      navigate("/login");
    }
  };

  const handleSwitchOrganization = async (companyId) => {
    try {
      setSwitching(companyId);
      const res = await api.staffSwitchOrganization(companyId);
      
      console.log("Switch organization response:", res);
      
      // Set the access token for the organization in API client
      setAccessToken(res.accessToken);
      
      // Set the token, user and company in AuthContext
      setStaffOrgAccess(res.accessToken, res.user, res.company);
      
      console.log("After setStaffOrgAccess - token:", res.accessToken);
      
      toast({
        title: t('staff.dashboard.switched'),
        description: t('staff.dashboard.switchedTo', { name: res.company.name }),
      });

      // Small delay to ensure state is updated before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      // Redirect to merchant dashboard
      navigate("/merchant");
    } catch (err) {
      toast({
        title: t('staff.dashboard.switchError'),
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setSwitching(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-muted flex items-center justify-center">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-background rounded-lg shadow p-4 mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{t('staff.dashboard.title')}</h1>
            {user && (
              <p className="text-sm text-muted-foreground mt-1">
                {user.firstName} {user.lastName} · {user.email}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/staff/security")}
              className="flex items-center gap-2"
            >
              {t('staff.dashboard.security')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              {t('nav.logout')}
            </Button>
          </div>
        </div>

        {/* Organizations Grid */}
        <div>
          <h2 className="text-lg font-semibold mb-4">
            {t('staff.dashboard.orgs.title', { count: organizations.length })}
          </h2>
          {organizations.length === 0 ? (
            <Card>
              <CardContent className="p-6 text-center text-muted-foreground">
                <Building2 className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
                <p>{t('staff.dashboard.orgs.none')}</p>
                <p className="text-sm mt-2">
                  {t('staff.dashboard.orgs.remoteAccessNote')}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {organizations.map((org) => (
                <Card key={org.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-3">
                      {org.logoUrl ? (
                        <img
                          src={org.logoUrl}
                          alt={org.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-blue-600" />
                        </div>
                      )}
                      <span className="text-base">{org.name}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-muted-foreground mb-4">
                      <div>
                        <span className="font-medium">{t('staff.dashboard.org.emailLabel')}</span> {org.email}
                      </div>
                      {org.sendmireinEnabled && (
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                          {t('staff.dashboard.org.sendmireinEnabled')}
                        </div>
                      )}
                    </div>
                    <Button
                      className="w-full flex items-center justify-center gap-2"
                      onClick={() => handleSwitchOrganization(org.id)}
                      disabled={switching === org.id}
                    >
                      {switching === org.id ? (
                        t('staff.dashboard.switching')
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4" />
                          {t('staff.dashboard.openDashboard')}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffDashboard;
